import React, { useState } from 'react';
import { Card, Button, Modal, Form } from 'react-bootstrap';
import { FileText, InfoCircle, BoxArrowUp, Clipboard, ClipboardCheck } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCollections, { CollectionModal } from '../../hooks/useCollections';
import openCapacitorSite from '../../utils/browserUtils';
import '../feed/FeedItem.css';

/**
 * MemberFeedItem
 *
 * A member-page-only copy of FeedItem. It exists so the member profile can
 * add a second body row (vote status/result) and route votes to their detail
 * page without touching the shared FeedItem that every feed, carousel, and
 * recents surface renders.
 *
 * Deliberately narrower than FeedItem: no recents source header, no
 * View More row, no spending variant. Add them here if the member page ever
 * needs them.
 *
 * Styling is intentionally shared via FeedItem.css and the same
 * .feed-item / .feed-item--{variant} class names, so themes stay in sync.
 *
 * @param {Object} props
 * @param {Object} props.item - Feed item data. Must carry the id used for routing.
 * @param {string} props.feedName - Collection feed name (English, per stored-data convention)
 * @param {string} props.branch - Collection branch
 * @param {string} props.topic - Collection topic
 * @param {string} props.buttonName - Label for the document button
 * @param {string} props.schema - Schema for routing
 * @param {string} props.table - Table for routing
 * @param {string} props.variant - Style variant, defaults to "default"
 * @param {React.ReactNode} props.bodyExtra - Optional extra body content under the date
 * @param {boolean} props.detailRoute - true (default) renders a "More" button that
 *   navigates to /feed/{schema}/{table}/{id}. Pass false for records with no in-app
 *   detail page; the button becomes "Source" and opens item.url.
 */
const MemberFeedItem = ({
  item,
  feedName,
  branch,
  topic,
  buttonName = "Document",
  schema,
  table,
  variant = "default",
  bodyExtra = null,
  detailRoute = true
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const {
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    closeModal,
    handleSave,
    isStorageAvailable,
    CollectionButton
  } = useCollections();

  const shareLink = `https://news.govglance.org/feed/${schema}/${table}/${item.id}`;

  const title = truncateText(item.status_title || item.title, 120);

  const docLink = item.document_link || (item.download?.pdfLink) || null;

  const formattedDate = formatDate(item.created_at);

  const handleDetailsClick = () => {
    navigate(`/feed/${schema}/${table}/${item.id}`);
  };

  const handleTitleClick = (e) => {
    e.preventDefault();
    openCapacitorSite(docLink || item.url, '_blank');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          url: shareLink,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      alert(t('common:shareUnsupported', 'Sharing is not supported on this device.'));
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink)
      .then(() => setLinkCopied(true))
      .catch((error) => console.error('Error copying link:', error));
  };

  const recentAction = item.actions ? getMostRecentActionDisplay(item.actions) : null;

  // IMPORTANT: pass the raw date. CollectionItem handles display formatting.
  const collectionItem = {
    title: title,
    url: item.url,
    docLink: docLink,
    date: item.created_at,
    feedName: feedName,
    branch: branch,
    topic: topic
  };

  return (
    <div className={`feed-item feed-item--${variant} mb-4`}>
      <Card>
        <Card.Title
          id="billText"
          className="card-title p-3"
          onClick={handleTitleClick}
          style={{ cursor: 'pointer' }}
        >
          {title}
        </Card.Title>

        <Card.Body id="billBodyText">
          {table === 'congressional_bills' && recentAction
            ? `${formatDate(recentAction.action_date)} ${recentAction.action_description}`
            : formattedDate}
        </Card.Body>

        {bodyExtra && (
          <Card.Body id="billBodyText">
            {bodyExtra}
          </Card.Body>
        )}

        <Card.Footer className="d-flex">
          {detailRoute ? (
            <Button
              className="clickable me-2"
              id="billLink"
              onClick={handleDetailsClick}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg> {t('common:more', 'More')}
            </Button>
          ) : (
            <Button
              className="clickable me-2"
              id="billLink"
              onClick={() => openCapacitorSite(item.url, '_blank')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg> {t('common:source', 'Source')}
            </Button>
          )}

          {docLink && buttonName && (
            <Button
              className="clickable me-2"
              id="viewBillButton"
              onClick={() => openCapacitorSite(docLink, "_blank")}
            >
              <FileText /> {buttonName}
            </Button>
          )}

          <CollectionButton
            item={collectionItem}
            className="me-2"
          />

          <Button
            className="clickable"
            id="shareButton"
            onClick={() => setShareModalOpen(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-send-horizontal-icon lucide-send-horizontal"><path d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z"/><path d="M6 12h16"/></svg>
          </Button>
        </Card.Footer>
      </Card>

      <Modal id="fullBillModal" show={shareModalOpen} size="xl" centered onHide={() => setShareModalOpen(false)}>
        <Modal.Header id="billModalHeader" closeButton>
          <h3>{t('common:share', 'Share')}</h3>
        </Modal.Header>
        <Modal.Body id="descriptionModalBody">
          <Form.Group id="noteModalForm">
            <Form.Control id="noteModalFeedItem" type="text" readOnly value={shareLink} />
          </Form.Group>
          <Modal.Footer id="shareFooter">
            <Button
              className="clickable me-2"
              id="addToCollectionButton"
              onClick={handleNativeShare}
            >
              <BoxArrowUp />
            </Button>
            <Button
              className="clickable"
              id="addToCollectionButton"
              onClick={handleCopyLink}
            >
              {linkCopied ? <ClipboardCheck /> : <Clipboard />}
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </Modal>

      <CollectionModal
        isModalOpen={isModalOpen}
        currentItem={currentItem}
        saveSuccess={saveSuccess}
        note={note}
        setNote={setNote}
        handleSave={handleSave}
        closeModal={closeModal}
        isStorageAvailable={isStorageAvailable}
      />
    </div>
  );
};

function truncateText(text, maxLength = 180) {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
}

function formatDate(inputDate) {
  if (!inputDate) return '';

  // If it's already in M-D-YYYY format, return as-is
  if (typeof inputDate === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(inputDate)) {
    return inputDate;
  }

  try {
    const date = new Date(inputDate);
    if (isNaN(date.getTime())) return '';

    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    const day = utcDate.getDate();
    const month = utcDate.getMonth() + 1;
    const year = utcDate.getFullYear();
    return `${month}-${day}-${year}`;
  } catch (error) {
    return '';
  }
}

function getMostRecentActionDisplay(actions) {
  if (!actions || actions.length === 0) return null;
  return actions.sort((a, b) => new Date(b.action_date) - new Date(a.action_date))[0];
}

export default MemberFeedItem;